import { DatePicker } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { genderOptions } from 'sections/LKSection/modules/UserModule/data'
import { CompanyDetailsViewModel } from 'sections/LKSection/modules/UserModule/stores/models/CompanyDetailsViewModel'
import { Citizenship, DocumentType, GENDER, UserModuleKeys } from 'sections/LKSection/modules/UserModule/types'
import { ReactSelect } from 'components/ui/Select/ReactSelect'
import { FormItem, Input } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { ALL_EXCEPT_NUMBERS_REGEXP } from 'shared/constants/regExp'
import { DATE_FORMAT_DEFAULT } from 'shared/constants/values'
import { BaseOption } from 'shared/types/types'

import { DepartmentCodeFormat, passport, passportIssuedPlaceholders, RequisitesCIS } from './requisites'

type OldPassportFormProps = {
  uiStore: CompanyDetailsViewModel
}

export const OldPassportForm = observer(({ uiStore }: OldPassportFormProps) => {
  const { t } = useTranslation()
  const citizenship = uiStore.selectedCitizenship.value
  const stylePicker: React.CSSProperties = { width: 200 }
  const getKey = (key: string) => `${UserModuleKeys.passportStep}.${key}`
  const getRequisitesKey = (key: string) => `${UserModuleKeys.requisites}.${key}`

  const handleSetDateOfIssue = (date: Dayjs, dateString: string | string[]) => {
    if (typeof dateString === 'string') {
      uiStore.setDateOfIssue(dateString)
    }
  }

  const handleSetDateOfBirthday = (date: Dayjs, dateString: string | string[]) => {
    if (typeof dateString === 'string') {
      uiStore.setDateOfBirthday(dateString)
    }
  }

  return (
    <>
      <FormItem
        label={passport[citizenship].label}
        name={'passport'}
        rules={[
          { required: true, message: t(getSharedKey('required')) },
          {
            min: passport[citizenship].maxLength,
            message: t(getSharedKey('minLength'), { count: passport[citizenship].maxLength }),
          },
        ]}
        normalize={(value) => {
          if (uiStore.selectedCitizenship.value === Citizenship.RU) {
            return value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
          } else {
            return value
          }
        }}
      >
        <Input
          maxLength={passport[citizenship].maxLength}
          placeholder={passport[citizenship].placeholder}
          value={uiStore.passport}
          onChange={(e) => uiStore.setPassport(e.target.value)}
        />
      </FormItem>
      {(citizenship === Citizenship.BY || citizenship === Citizenship.KZ) && (
        <FormItem
          label={t(getRequisitesKey(RequisitesCIS[citizenship][uiStore.documentType].label))}
          name={'identificationNumber'}
          rules={[
            {
              required: true,
              message: t(getSharedKey('required')),
            },
            {
              min: RequisitesCIS[citizenship][uiStore.documentType].maxLength,
              message: t(getSharedKey('minLength'), {
                count: RequisitesCIS[citizenship][uiStore.documentType].maxLength,
              }),
            },
          ]}
          normalize={(value) => {
            if (
              uiStore.selectedCitizenship.value === Citizenship.KZ ||
              uiStore.selectedCitizenship.value === Citizenship.KG ||
              uiStore.selectedCitizenship.value === Citizenship.TJ ||
              uiStore.selectedCitizenship.value === Citizenship.GE
            ) {
              return value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
            } else {
              return value
            }
          }}
        >
          <Input
            maxLength={RequisitesCIS[citizenship][uiStore.documentType].maxLength}
            placeholder={RequisitesCIS[citizenship][uiStore.documentType].placeholder}
            value={uiStore.identificationNumber}
            onChange={(e) => uiStore.setIdentificationNumber(e.target.value)}
          />
        </FormItem>
      )}
      <FormItem
        label={'Дата выдачи'}
        name={'dateOfIssue'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
      >
        <DatePicker
          onChange={handleSetDateOfIssue}
          format={DATE_FORMAT_DEFAULT}
          value={uiStore.dateOfIssue ? dayjs(uiStore.dateOfIssue, DATE_FORMAT_DEFAULT) : null}
          placeholder={'дд.мм.гггг'}
          maxDate={dayjs()}
          style={stylePicker}
        />
      </FormItem>
      {citizenship === Citizenship.RU && (
        <FormItem
          label={DepartmentCodeFormat[citizenship][DocumentType.passport].label}
          name={'departmentCode'}
          rules={[
            { required: true, message: t(getSharedKey('required')) },
            {
              min: DepartmentCodeFormat[citizenship][DocumentType.passport].maxLength,
              message: t(getSharedKey('minLength'), {
                count: DepartmentCodeFormat[citizenship][DocumentType.passport].maxLength,
              }),
            },
          ]}
          normalize={(value) => {
            if (uiStore.selectedCitizenship.value === Citizenship.RU) {
              const digits = value.replace(/\D/g, '')

              if (digits.length === 6) {
                return digits.slice(0, 3) + '-' + digits.slice(3, 6)
              } else {
                return digits
              }
            } else if (
              uiStore.selectedCitizenship.value === Citizenship.KG ||
              uiStore.selectedCitizenship.value === Citizenship.UZ
            ) {
              return value
            } else {
              return value?.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
            }
          }}
        >
          <Input
            placeholder={DepartmentCodeFormat[citizenship][DocumentType.passport].placeholder}
            value={uiStore.departmentCode}
            onChange={(e) => uiStore.setDepartmentCode(e.target.value)}
            maxLength={DepartmentCodeFormat[citizenship][DocumentType.passport].maxLength}
          />
        </FormItem>
      )}
      {citizenship === Citizenship.RU && (
        <FormItem label={'Пол'}>
          <ReactSelect
            placeholder={'Пол'}
            options={genderOptions}
            value={genderOptions.find((option) => option.value === uiStore.gender)}
            onChange={(item) => {
              const newItem = item as BaseOption<GENDER>
              uiStore.setGender(newItem?.value ?? genderOptions[0].value)
            }}
          />
        </FormItem>
      )}
      <FormItem
        label={'Дата рождения'}
        name={'dateOfBirthday'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
      >
        <DatePicker
          onChange={handleSetDateOfBirthday}
          format={DATE_FORMAT_DEFAULT}
          value={uiStore.dateOfBirthday ? dayjs(uiStore.dateOfBirthday, DATE_FORMAT_DEFAULT) : null}
          placeholder={'дд.мм.гггг'}
          maxDate={dayjs()}
          style={stylePicker}
        />
      </FormItem>

      <FormItem
        label={'Место рождения'}
        name={'placeOfBirth'}
        rules={[{ required: true, message: t(getSharedKey('required')) }]}
      >
        <Input
          placeholder={t(getKey('Место рождения'))}
          value={uiStore.placeOfBirth}
          onChange={(e) => uiStore.setPlaceOfBirth(e.target.value)}
        />
      </FormItem>

      {citizenship !== Citizenship.AM && (
        <FormItem
          label={'Кем выдан'}
          name={'passportIssued'}
          rules={[{ required: true, message: t(getSharedKey('required')) }]}
        >
          <Input
            placeholder={passportIssuedPlaceholders[citizenship][uiStore.documentType]}
            value={uiStore.passportIssued}
            onChange={(e) => uiStore.setPassportIssued(e.target.value)}
          />
        </FormItem>
      )}

      {citizenship === Citizenship.RU && (
        <>
          <FormItem
            label={'Адрес регистрации'}
            name={'registrationAddress'}
            rules={[{ required: true, message: t(getSharedKey('required')) }]}
          >
            <Input
              placeholder={'Registration address'}
              value={uiStore.registrationAddress}
              onChange={(e) => uiStore.setRegistrationAddress(e.target.value)}
            />
          </FormItem>
          <FormItem
            label={'Фактическое место проживания'}
            name={'permanentResidenceAddress'}
            rules={[{ required: true, message: t(getSharedKey('required')) }]}
          >
            <Input
              placeholder="место проживания"
              value={uiStore.permanentResidenceAddress}
              onChange={(e) => uiStore.setPermanentResidenceAddress(e.target.value)}
            />
          </FormItem>
        </>
      )}
    </>
  )
})
